/**
 * Erkennt, ob das App-Stylesheet im Browser tatsächlich greift.
 *
 * Drei unabhängige Sentinels, kombiniert per UND-Verknüpfung — nur wenn
 * ALLE drei versagen, gilt das Stylesheet als fehlend. So vermeiden wir
 * False Positives durch Tailwind v4 Purging (`.hidden` kann aus dem
 * Bundle entfernt werden) oder durch cross-origin StyleSheet-Objekte
 * (deren `cssRules` aus Sicherheitsgründen nicht lesbar sind).
 *
 *   1) Design-Token: `--background` aus `src/styles.css` ist auflösbar.
 *      Garantiert vorhanden, sobald unser Stylesheet geladen wurde,
 *      unabhängig vom Tailwind-Tree-Shaking.
 *   2) Utility-Klasse: `.hidden` setzt `display: none`. Klassischer
 *      Tailwind-Indikator, kann aber gepurged sein.
 *   3) Stylesheet-Regeln: irgendein same-origin Stylesheet liefert
 *      mindestens eine Regel.
 */
export type CssSentinelResult = {
  hasDesignTokens: boolean;
  hiddenWorks: boolean;
  loadedSheets: number;
  cssMissing: boolean;
};

export type CssSentinelDeps = {
  doc: Document;
  // Erlaubt es den Tests, getComputedStyle zu stubben, ohne globale
  // window-Stubs zu installieren.
  getComputedStyle: (el: Element) => CSSStyleDeclaration;
};

const DESIGN_TOKEN = "--background";

export function runCssSentinel(deps: CssSentinelDeps): CssSentinelResult {
  const { doc, getComputedStyle } = deps;

  // 1) Design-Token-Probe
  const rootStyle = getComputedStyle(doc.documentElement);
  const hasDesignTokens = rootStyle.getPropertyValue(DESIGN_TOKEN).trim().length > 0;

  // 2) `.hidden`-Utility-Klasse
  const probe = doc.createElement("div");
  probe.className = "hidden";
  probe.style.cssText = "position:absolute;left:-9999px;top:-9999px;";
  doc.body.appendChild(probe);
  const hiddenWorks = getComputedStyle(probe).display === "none";
  doc.body.removeChild(probe);

  // 3) Stylesheets mit Regeln
  let loadedSheets = 0;
  for (const sheet of Array.from(doc.styleSheets)) {
    try {
      // cssRules kann bei cross-origin Sheets eine SecurityError werfen.
      if (sheet.cssRules && sheet.cssRules.length > 0) loadedSheets++;
    } catch {
      // cross-origin → überspringen, kein Hinweis auf fehlendes Stylesheet
    }
  }

  const cssMissing = !hasDesignTokens && !hiddenWorks && loadedSheets === 0;

  return { hasDesignTokens, hiddenWorks, loadedSheets, cssMissing };
}
